import { useContext } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { SidebarContext } from 'src/core/contexts/SidebarContext';
import { Logo } from 'src/core/components';
import { MenuItems } from 'src/core/types/appConfig';
import { drHerancaBranding } from 'src/drheranca/branding';

import { Box, Drawer, Hidden, CircularProgress } from '@mui/material';

import { styled } from '@mui/material/styles';
import SidebarMenu from './SidebarMenu';

interface SidebarProps {
  menuItems?: MenuItems[];
  isLoading?: boolean;
}

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
        width: ${collapsed ? '80px' : theme.sidebar.width};
        color: ${drHerancaBranding.color.text.primary};
        background: ${drHerancaBranding.color.surface.subtle};
        box-shadow: ${theme.sidebar.boxShadow};
        border-right: 1px solid ${drHerancaBranding.color.border.default};
        height: 100%;
        transition: width 0.3s ease;
        
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            position: fixed;
            z-index: 10;
            border-top-right-radius: ${theme.general.borderRadius};
            border-bottom-right-radius: ${theme.general.borderRadius};
        }
`
);

const TopSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
        display: flex;
        height: 88px;
        align-items: center;
        justify-content: flex-start;
        margin-left: 12px;
        margin-right: 5px;
        border-bottom: 1px solid ${drHerancaBranding.color.border.default};
        overflow: hidden;
`
);

function Sidebar({ menuItems, isLoading }: SidebarProps) {
  const { sidebarToggle, toggleSidebar, sidebarCollapsed } = useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();

  // Show loading indicator if menu items are being loaded
  if (isLoading) {
    return (
      <>
        <Hidden lgDown>
          <Box
            className="no-print"
            sx={{
              position: 'fixed',
              left: 0,
              top: 0,
              height: '100vh',
              width: sidebarCollapsed ? 80 : 'auto',
              zIndex: 10,
              transition: 'width 0.3s ease',
            }}
          >
            <SidebarWrapper collapsed={sidebarCollapsed}>
              <Scrollbars autoHide>
                <TopSection collapsed={sidebarCollapsed}>
                  <Logo />
                </TopSection>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress size={24} />
                </Box>
              </Scrollbars>
            </SidebarWrapper>
          </Box>
        </Hidden>
        <Hidden lgUp>
          <Drawer
            anchor="left"
            open={sidebarToggle}
            onClose={closeSidebar}
            variant="temporary"
            elevation={9}
          >
            <SidebarWrapper>
              <Scrollbars autoHide>
                <TopSection>
                  <Logo />
                </TopSection>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress size={24} />
                </Box>
              </Scrollbars>
            </SidebarWrapper>
          </Drawer>
        </Hidden>
      </>
    );
  }

  return (
    <>
      {/* Desktop: Fixed drawer */}
      <Hidden lgDown>
        <Box
          className="no-print"
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            width: sidebarCollapsed ? 80 : 'auto',
            zIndex: 10,
            transition: 'width 0.3s ease',
          }}
        >
          <SidebarWrapper collapsed={sidebarCollapsed}>
            <Scrollbars autoHide>
              <TopSection collapsed={sidebarCollapsed}>
                <Logo />
              </TopSection>
              <SidebarMenu menuItems={menuItems} />
            </Scrollbars>
          </SidebarWrapper>
        </Box>
      </Hidden>
      {/* Mobile: Temporary drawer */}
      <Hidden lgUp>
        <Drawer
          anchor="left"
          open={sidebarToggle}
          onClose={closeSidebar}
          variant="temporary"
          elevation={9}
        >
          <SidebarWrapper>
            <Scrollbars autoHide>
              <TopSection>
                <Logo />
              </TopSection>
              <SidebarMenu menuItems={menuItems} />
            </Scrollbars>
          </SidebarWrapper>
        </Drawer>
      </Hidden>
    </>
  );
}

export default Sidebar;
